import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { actualizarEstadoCaptchaV3, actualizarTokenCaptchaV3 } from 'store';
import { datoNoValido } from 'utils/colorBallsUtils';
import {
  CADENA_VACIA,
  FORZAR_FALLBACK_CAPTCHA_V2,
  SIN_FORZAR_FALLBACK_CAPTCHA_V2,
} from 'utils/constants';

/**
 * Estado y callbacks compartidos de captcha v3/v2 para suscripción TER
 * (modal del mapa y acordeón).
 * @param tieneClaveCaptchaV3 indica si hay site key v3 en el entorno
 */
const useCaptchaSuscripcionTer = (tieneClaveCaptchaV3: boolean) => {
  const dispatch = useDispatch();

  /**
   * Evita que el fallback a v2 apague un v3 que ya entregó token.
   */
  const captchaV3ListoRef = useRef(SIN_FORZAR_FALLBACK_CAPTCHA_V2);

  /**
   * Token generado por el captcha v2.
   */
  const [tokenCaptchaV2, setTokenCaptchaV2] = useState(CADENA_VACIA);

  /**
   * Monta CaptchaV3 solo mientras se intenta obtener token; al fallar se desmonta.
   */
  const [intentarCaptchaV3, setIntentarCaptchaV3] = useState(tieneClaveCaptchaV3);

  /**
   * Muestra el checkbox v2 solo si no hay clave v3 o si v3 falló / no entregó token.
   */
  const [mostrarCaptchaV2, setMostrarCaptchaV2] = useState(!tieneClaveCaptchaV3);

  /**
   * Pide reiniciar el captcha v2 después de un envío fallido.
   */
  const [reiniciarCaptchaState, setReiniciarCaptchaState] = useState(SIN_FORZAR_FALLBACK_CAPTCHA_V2);

  /**
   * Activa el checkbox v2 y deja de montar v3.
   * @param forzar con FORZAR_FALLBACK_CAPTCHA_V2 aplica aunque v3 ya hubiera entregado token.
   */
  const activarFallbackCaptchaV2 = useCallback(
    (forzar = SIN_FORZAR_FALLBACK_CAPTCHA_V2) => {
      if (captchaV3ListoRef.current && !forzar) {
        return;
      }
      captchaV3ListoRef.current = SIN_FORZAR_FALLBACK_CAPTCHA_V2;
      setTokenCaptchaV2(CADENA_VACIA);
      setIntentarCaptchaV3(SIN_FORZAR_FALLBACK_CAPTCHA_V2);
      setMostrarCaptchaV2(FORZAR_FALLBACK_CAPTCHA_V2);
      dispatch(actualizarTokenCaptchaV3(CADENA_VACIA));
      dispatch(actualizarEstadoCaptchaV3(SIN_FORZAR_FALLBACK_CAPTCHA_V2));
    },
    [dispatch]
  );

  /**
   * Guarda el token v3 una sola vez; evita re-dispatch si Google vuelve a llamar onVerify.
   * @param token token emitido por Google reCAPTCHA v3
   */
  const onVerifyCaptchaV3 = useCallback(
    (token: string) => {
      if (datoNoValido(token) || captchaV3ListoRef.current) {
        return;
      }
      captchaV3ListoRef.current = FORZAR_FALLBACK_CAPTCHA_V2;
      setIntentarCaptchaV3(FORZAR_FALLBACK_CAPTCHA_V2);
      setMostrarCaptchaV2(SIN_FORZAR_FALLBACK_CAPTCHA_V2);
      dispatch(actualizarTokenCaptchaV3(token));
      dispatch(actualizarEstadoCaptchaV3(FORZAR_FALLBACK_CAPTCHA_V2));
    },
    [dispatch]
  );

  /**
   * Restaura el estado de captchas al abrir/cerrar el formulario.
   */
  const reiniciarEstadoCaptchas = useCallback(() => {
    setTokenCaptchaV2(CADENA_VACIA);
    captchaV3ListoRef.current = SIN_FORZAR_FALLBACK_CAPTCHA_V2;
    setIntentarCaptchaV3(tieneClaveCaptchaV3);
    setMostrarCaptchaV2(!tieneClaveCaptchaV3);
    dispatch(actualizarTokenCaptchaV3(CADENA_VACIA));
    dispatch(actualizarEstadoCaptchaV3(SIN_FORZAR_FALLBACK_CAPTCHA_V2));
  }, [dispatch, tieneClaveCaptchaV3]);

  return {
    captchaV3ListoRef,
    tokenCaptchaV2,
    setTokenCaptchaV2,
    intentarCaptchaV3,
    mostrarCaptchaV2,
    reiniciarCaptchaState,
    setReiniciarCaptchaState,
    activarFallbackCaptchaV2,
    onVerifyCaptchaV3,
    reiniciarEstadoCaptchas,
  };
};

export default useCaptchaSuscripcionTer;
